import React, { useEffect, useState } from 'react'
import { Redirect, useParams } from 'react-router-dom'
import Avatar from '@material-ui/core/Avatar'
import Box from '@material-ui/core/Box'
import ButtonBase from '@material-ui/core/ButtonBase'
import LinearProgress from '@material-ui/core/LinearProgress'
import Tooltip from '@material-ui/core/Tooltip'
import Typography from '@material-ui/core/Typography'
import { PageHeader } from 'components/page-header'
import InfoAlert from 'components/info-alert'
import AccountRouterLink from 'components/account-router-link'
import MessageIcon from 'components/material-symbol/icons/Message'
import ScheduleIcon from 'components/material-symbol/icons/Schedule'
import VideocamIcon from 'components/material-symbol/icons/Videocam'
import StreamSendMessageButton from 'components/stream-messaging/send-message-button'
import { useIsMobile } from 'lib/hooks'
import useCampaignStyles from 'pages/pga-coach/campaigns/styles'
import LessonTypeDetails from 'pages/pga-coach/coach-analytics/components/lesson-type-details'
import { GET_LESSON_TYPE_DETAILS_WITH_VIRTUAL_COACHING } from 'pages/pga-coach/bookings/booking-types/queries'
import { SettingsSection } from 'pages/pga-coach/settings/settings-components'
import useSettingsStyles from 'pages/pga-coach/settings/settings.styles'
import { useVirtualCoachingRequest } from './api-hooks'
import { useStyles } from './styles'
import VirtualCoachingCoachPlusGate from './coach-plus-gate'
import LoadError from './load-error'
import SwingVideoActions from './swing-video-actions'
import SwingVideoGallery from './swing-video-gallery'
import RequestStatusChip from './request-status-chip'
import ReplyForm from './reply-form'
import RequestResponse from './request-response'
import StudentFeedback from './student-feedback'
import { trackVirtualCoachingEvent, VIRTUAL_COACHING_EVENTS } from './analytics'
import {
  formatDate,
  formatDateTime,
  getAgingLabel,
  getRequestsPath,
  getStudentName,
  isOverdue,
  REQUEST_STATUS,
  VIDEO_PLAYER_PROPS,
} from './utils'

// A stat tile. With `onClick` it is a button, and with `to` an account link
// that keeps `return_to` (standard ButtonBase hover, focus and ripple), named
// by `ariaLabel`.
const StatTile = ({ label, children, onClick, to, ariaLabel }) => {
  const classes = useStyles()
  const interactive = Boolean(onClick || to)
  const content = (
    <>
      <Typography
        component={interactive ? 'span' : 'p'}
        className={classes.statTileLabel}
      >
        {label}
      </Typography>
      <Typography
        component={interactive ? 'span' : 'div'}
        className={classes.statTileValue}
      >
        {children}
      </Typography>
    </>
  )

  if (!interactive) return <Box className={classes.statTile}>{content}</Box>

  return (
    <ButtonBase
      className={`${classes.statTile} ${classes.statTileButton}`}
      aria-label={ariaLabel}
      onClick={onClick}
      {...(to ? { component: AccountRouterLink, to } : {})}
    >
      {content}
    </ButtonBase>
  )
}

const ContextItem = ({ label, value }) => {
  const classes = useStyles()
  if (!value) return null
  return (
    <Box className={classes.contextItem}>
      <Typography className={classes.statTileLabel}>{label}</Typography>
      <Typography className={classes.contextValue}>{value}</Typography>
    </Box>
  )
}

const SwingVideo = ({ video, golfer, showAnalyze, trackedProps }) => {
  const classes = useStyles()
  return (
    <Box>
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <video
        className={classes.mediaVideo}
        src={video.url}
        poster={video.thumbnailUrl || undefined}
        controls
        playsInline
        {...VIDEO_PLAYER_PROPS}
        aria-label={`${golfer}'s swing video`}
      />
      <SwingVideoActions
        video={video}
        downloadInDialog
        showAnalyze={showAnalyze}
        trackedProps={trackedProps}
      />
    </Box>
  )
}

const VirtualCoachingRequestDetails = () => {
  const classes = useStyles()
  const settingsClasses = useSettingsStyles()
  const [offeringOpen, setOfferingOpen] = useState(false)
  // Header chrome (round icon avatar, meta line, tightened back-arrow grid and
  // pill actions) is the Campaigns details header.
  const campaignClasses = useCampaignStyles()
  const isMobile = useIsMobile()
  const { requestId } = useParams()
  const { data, loading, error, refetch } = useVirtualCoachingRequest(requestId)
  const request = data?.virtualCoachingRequest

  useEffect(() => {
    if (!request?.id) return
    trackVirtualCoachingEvent(VIRTUAL_COACHING_EVENTS.REQUEST_VIEWED, {
      requestId: request.id,
      status: request.status,
      submittedAt: request.submittedAt,
    })
    // Track one view per request, not per refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request?.id])

  const crumbs = [{ label: 'Virtual Coaching', to: getRequestsPath() }]

  if (loading && !request) return <LinearProgress color="primary" />
  if (error && !request) {
    return (
      <>
        <PageHeader title="Virtual Coaching" crumbs={crumbs} />
        <Box className={settingsClasses.contentContainer}>
          <LoadError
            error={error}
            title="We couldn't load this request"
            onRetry={() => refetch()}
          />
        </Box>
      </>
    )
  }
  if (!request) return <Redirect to="/404" />

  const golfer = getStudentName(request.student)
  const firstName = request.student?.firstName || 'the golfer'
  const overdue = isOverdue(request)
  const isReady = request.status === REQUEST_STATUS.READY_TO_ANALYZE
  const isComplete = request.status === REQUEST_STATUS.COMPLETE
  const hasContext = request.golfLevel || request.struggle || request.goal
  const trackedProps = { requestId: request.id }
  // The golfer can upload up to 3 videos; show every one that has a URL.
  const videos = (request.videos || []).filter(video => video?.url)
  const deliveredAt = request.completedAt || request.response?.sharedAt
  const transactionId = request.transaction?.id
  const studentExternalId = request.student?.externalId
  const messageName = `Message ${request.student?.firstName || 'golfer'}`

  // Header actions, as on Campaigns. They collapse to icon-only pills (keeping
  // their accessible names) when the title needs the room.
  const renderActions = ({ compact }) => {
    const actionProps = (label, icon, accessibleName = label) =>
      compact
        ? {
          className: `${campaignClasses.pillButton} ${classes.compactButton}`,
          'aria-label': accessibleName,
          children: icon,
        }
        : {
          className: campaignClasses.pillButton,
          'aria-label': accessibleName,
          startIcon: icon,
          children: label,
        }
    const withTooltip = (label, button) =>
      compact ? (
        <Tooltip title={label}>
          <span>{button}</span>
        </Tooltip>
      ) : (
        button
      )

    return (
      <>
        {studentExternalId
          ? withTooltip(
            messageName,
            <StreamSendMessageButton
              userIds={[studentExternalId]}
              variant="outlined"
              color="primary"
              classes={{ label: classes.buttonLabel }}
              {...actionProps(
                'Message',
                <MessageIcon aria-hidden />,
                messageName,
              )}
            />,
          )
          : null}
      </>
    )
  }

  return (
    <>
      <PageHeader
        title={golfer}
        backTo={getRequestsPath()}
        backLabel="Back to Virtual Coaching"
        avatar={
          isMobile ? (
            undefined
          ) : (
            <Avatar className={campaignClasses.headerAvatar}>
              <VideocamIcon aria-hidden />
            </Avatar>
          )
        }
        subtitle={
          <Box component="span" className={campaignClasses.headerMeta}>
            <RequestStatusChip status={request.status} overdue={overdue} />
            {isReady && request.dueAt ? (
              <Typography
                component="span"
                className={`${classes.statusMeta} ${
                  overdue ? classes.statusMetaOverdue : ''
                }`}
              >
                <ScheduleIcon aria-hidden />
                Deliver by {formatDate(request.dueAt)} ·{' '}
                {getAgingLabel(request)}
              </Typography>
            ) : null}
            {isComplete && deliveredAt ? (
              <Typography component="span" className={classes.statusMeta}>
                Delivered {formatDate(deliveredAt)}
              </Typography>
            ) : null}
          </Box>
        }
        keepActionsInline
        actions={studentExternalId ? renderActions : undefined}
        gridClassName={campaignClasses.headerGrid}
      />
      <Box className={settingsClasses.contentContainer}>
        <Box className={classes.statGrid}>
          <StatTile
            label="Offering"
            onClick={
              request.offering?.id ? () => setOfferingOpen(true) : undefined
            }
            ariaLabel={`View offering details: ${request.offering?.title}`}
          >
            {request.offering?.title || '—'}
          </StatTile>
          <StatTile
            label="Purchased"
            to={
              transactionId
                ? `/account/payments/transactions/${transactionId}`
                : undefined
            }
            ariaLabel={`View transaction: purchased ${formatDateTime(
              request.purchasedAt,
            )}`}
          >
            {formatDateTime(request.purchasedAt)}
          </StatTile>
          {request.submittedAt ? (
            <StatTile label="Video submitted">
              {formatDateTime(request.submittedAt)}
            </StatTile>
          ) : null}
        </Box>

        {request.status === REQUEST_STATUS.PURCHASED ? (
          <InfoAlert>
            <Typography variant="body2">
              {golfer} has not uploaded a swing video yet. You will get an email
              and a notification when it is ready to analyze.
            </Typography>
          </InfoAlert>
        ) : null}

        {videos.length > 0 ? (
          <SettingsSection
            eyebrow={videos.length > 1 ? 'Swing videos' : 'Swing video'}
            classes={settingsClasses}
          >
            <Box className={classes.sectionBody}>
              {videos.length > 1 ? (
                <SwingVideoGallery
                  videos={videos}
                  golfer={golfer}
                  showAnalyze={!isComplete}
                  trackedProps={trackedProps}
                />
              ) : (
                <SwingVideo
                  video={videos[0]}
                  golfer={golfer}
                  showAnalyze={!isComplete}
                  trackedProps={trackedProps}
                />
              )}
            </Box>
          </SettingsSection>
        ) : null}

        {videos.length > 0 && hasContext ? (
          <SettingsSection
            eyebrow={`What ${firstName} shared`}
            classes={settingsClasses}
          >
            <Box className={`${classes.sectionBody} ${classes.contextList}`}>
              <ContextItem label="Golf level" value={request.golfLevel} />
              <ContextItem label="Struggling with" value={request.struggle} />
              <ContextItem label="Hoping to achieve" value={request.goal} />
            </Box>
          </SettingsSection>
        ) : null}

        {isReady ? <ReplyForm request={request} /> : null}
        {isComplete ? (
          <>
            <RequestResponse request={request} />
            <StudentFeedback request={request} />
          </>
        ) : null}
      </Box>
      {offeringOpen ? (
        <LessonTypeDetails
          lessonTypeId={request.offering.id}
          query={GET_LESSON_TYPE_DETAILS_WITH_VIRTUAL_COACHING}
          onClose={() => setOfferingOpen(false)}
        />
      ) : null}
    </>
  )
}

const VirtualCoachingRequestDetailsPage = () => (
  <VirtualCoachingCoachPlusGate>
    <VirtualCoachingRequestDetails />
  </VirtualCoachingCoachPlusGate>
)

export default VirtualCoachingRequestDetailsPage
